import React from 'react';
import { Link } from 'react-router-dom';
import { Code2, Lock, Webhook, Database, ArrowRight } from 'lucide-react';
import PageLayout, { PageHero } from '../components/PageLayout';
import { ApiArt } from '../components/illustrations';

const ENDPOINTS = [
  { method: 'GET',   path: '/api/v1/leads',               desc: 'List all leads with pagination and filters' },
  { method: 'POST',  path: '/api/v1/leads',               desc: 'Create a new lead' },
  { method: 'PATCH', path: '/api/v1/leads/:id',           desc: 'Update lead fields or status' },
  { method: 'GET',   path: '/api/v1/campaigns',           desc: 'List all campaigns' },
  { method: 'POST',  path: '/api/v1/campaigns/:id/start', desc: 'Start a campaign' },
  { method: 'GET',   path: '/api/v1/agents',              desc: 'List agents and current status' },
  { method: 'GET',   path: '/api/v1/calls',               desc: 'Call history with filters' },
  { method: 'POST',  path: '/api/v1/webhooks',            desc: 'Register a webhook endpoint' },
];

/* Verb colour is categorical, so it maps onto the accent set — and each
   label sits on its -strong pair, never the vivid fill. */
const METHOD_CHIP = {
  GET:    'chip--info',
  POST:   'chip--success',
  PATCH:  'chip--warning',
  DELETE: 'chip--danger',
};

const FEATURES = [
  { icon: Lock,     accent: 'accent-1', title: 'Bearer token auth', desc: 'All API calls require a Bearer token from your WASLX account settings.' },
  { icon: Webhook,  accent: 'accent-5', title: 'Webhooks',          desc: 'Subscribe to real-time events — new lead, call ended, disposition set, and more.' },
  { icon: Database, accent: 'accent-4', title: 'Bulk operations',   desc: 'Import thousands of leads, export call history and sync your CRM through batch endpoints.' },
];

const ApiReference = () => (
  <PageLayout
    title="API Reference — REST API for Leads, Campaigns & Webhooks"
    description="WASLX REST API documentation. Integrate leads, campaigns, agents, call logs, and webhooks into your existing systems. Request API access to get started."
    path="/api-reference"
  >
    <PageHero
      eyebrow="Beta — API in preview"
      accent="accent-6"
      title={
        <>
          WASLX <span className="display-accent">API reference</span>
        </>
      }
      sub="The WASLX REST API lets you integrate calling operations into your own systems. It is available to beta partners — reach out to get access."
      art={ApiArt}
    />

    <section className="section section--muted">
      <div className="container-page">
        <div className="mx-auto max-w-4xl">
          {/* A code panel is a stage: it stays dark in both themes, the same
              way a terminal or a video letterbox does. */}
          <div className="rounded-xl bg-stage p-6">
            <p className="text-2xs font-bold uppercase tracking-[0.12em] text-stage-muted">Base URL</p>
            <code className="mt-3 block font-mono text-lg text-primary-light">
              https://api.waslx.com/v1
            </code>
            <p className="mt-3 text-[0.9375rem] text-stage-muted">
              All requests must include{' '}
              <code className="rounded-sm bg-stage-tile px-1.5 py-0.5 font-mono text-sm text-stage-on">
                Authorization: Bearer &lt;token&gt;
              </code>
            </p>
          </div>

          <div className="stagger-children mt-10 grid gap-5 md:grid-cols-3">
            {FEATURES.map(feature => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.title}
                  className={`${feature.accent} card card--hover animate-fade-up`}
                >
                  <span className="icon-tile">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="card-title mt-4">{feature.title}</h3>
                  <p className="card-body">{feature.desc}</p>
                </div>
              );
            })}
          </div>

          <h2 className="mb-4 mt-12 text-2xl font-bold text-content">Endpoints</h2>
          <div className="overflow-hidden rounded-lg bg-surface shadow-soft">
            {ENDPOINTS.map(endpoint => (
              <div
                key={`${endpoint.method}-${endpoint.path}`}
                className="flex items-center gap-4 border-b border-line px-5 py-3.5 last:border-b-0"
              >
                <span
                  className={`chip ${METHOD_CHIP[endpoint.method]} w-[68px] flex-shrink-0 justify-center font-mono font-bold`}
                >
                  {endpoint.method}
                </span>
                <code className="w-64 flex-shrink-0 font-mono text-[0.9375rem] text-content">
                  {endpoint.path}
                </code>
                <span className="hidden text-[0.9375rem] text-content-muted sm:block">
                  {endpoint.desc}
                </span>
              </div>
            ))}
          </div>

          <div className="accent-6 card card--tint mt-12 text-center">
            <span className="icon-tile icon-tile--lg icon-tile--solid mx-auto">
              <Code2 className="h-6 w-6" />
            </span>
            <h3 className="mt-4 text-xl font-bold text-content">Get API access</h3>
            <p className="mx-auto mt-2 max-w-md text-[0.9375rem] text-content-muted">
              The API is available to beta partners. Contact us for your token, the full docs and a
              Postman collection.
            </p>
            <Link to="/contact" className="btn-pill btn-pill--primary mt-6">
              Request API access
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  </PageLayout>
);

export default ApiReference;
